import BasicAPI from "./basic";

export class FooterSectionChildren extends BasicAPI {
    /**
     * @param children {Object} - Данные с апи запроса
     */
    constructor(children) {
        super();
        this.icon = children.icon
        this.href = children.href
        this.text = children.text
        this.type = children.type
    }

    /**
     * Валидация полей
     * @param expect {Expect<{}>}
     * @param request {APIRequestContext}
     */
    async validate({expect, request}) {
        expect(this.type).toBeTruthy()

        if (this.type === 'feedback' || this.type === 'text') {
            expect(this.text).toBeTruthy()
        } else {
            expect(this.icon).toBeTruthy();
        }

        if (this.type === 'text' || this.type === 'icon') {
            expect(this.href).toBeTruthy();
        } else {
            expect(this.href).toBeFalsy();
        }

        if (this.icon) {
            expect((await request.get(this.icon)).ok()).toBeTruthy();
        }
    }
}

export class FooterSection extends BasicAPI {
    /**
     * @param section {Object} - Данные с апи запроса
     */
    constructor(section) {
        super();
        this.title = section.title;
        this.text = section.text;

        if (section.children) {
            /**
             * @type {FooterSectionChildren[]}
             */
            this.children = []

            for (let children of section.children) {
                this.children.push(new FooterSectionChildren(children))
            }
        }
    }

    /**
     * Валидация полей
     * @param expect {Expect<{}>}
     * @param request {APIRequestContext}
     * @param test {TestType<PlaywrightTestArgs & PlaywrightTestOptions, PlaywrightWorkerArgs & PlaywrightWorkerOptions>}
     */
    async validate({expect, request, test}) {
        expect(this.title).toBeTruthy()
        expect(this.children).toBeTruthy()

        let index = 0

        for (const child of this.children) {
            await test.step(`Проверка элемента "${index}" ${child.text ? `(${child.text})` : ''}`, async () => {
                await child.validate({expect, request})
            })

            index++
        }
    }
}

export class FooterData extends BasicAPI {
    /**
     * @param config {Object} - Данные с апи запроса
     */
    constructor (config = {}) {
        super();
        /**
         * @type {FooterSection[]}
         */
        this.sections =  []

        if (config.sections) {
            for (let section of config.sections) {
                this.sections.push(new FooterSection(section))
            }
        }
    }

    /**
     * Валидация полей
     * @param expect {Expect<{}>}
     * @param request {APIRequestContext}
     * @param test {TestType<PlaywrightTestArgs & PlaywrightTestOptions, PlaywrightWorkerArgs & PlaywrightWorkerOptions>}
     */
    async validate({expect, request, test}) {
        for (const section of this.sections) {
            await test.step(`Проверка секции ${section.title}`, async () => {
                await section.validate({expect, request, test})
            })
        }
    }
}

export class SettingsSign extends BasicAPI {
    /**
     * @param sign {Object} - Данные с апи запроса
     */
    constructor(sign) {
        super();
        this.dataProcessingPolicy = sign.dataProcessingPolicy
        this.footerText = sign.footerText
    }

    /**
     * Валидация корректности полей
     * @param expect {Expect<{}>}
     * @param request {APIRequestContext}
     */
    async validate({expect, request}) {
        expect(this.dataProcessingPolicy).toBeTruthy()
        expect(this.footerText).toBeTruthy()
        expect((await request.get(this.dataProcessingPolicy)).ok()).toBeTruthy();
    }
}

export class SettingsBanner extends BasicAPI {
    /**
     * @param banner {Object} - Данные с апи запроса
     */
    constructor(banner) {
        super();
        this.topLine = banner.topLine
        this.bottomLine = banner.bottomLine
        this.img = banner.img ? new SettingsBannerImages(banner.img) : null
    }

    /**
     * Валидация полей
     * @param expect {Expect<{}>}
     * @param request {APIRequestContext}
     * @param test {TestType<PlaywrightTestArgs & PlaywrightTestOptions, PlaywrightWorkerArgs & PlaywrightWorkerOptions>}
     */
    async validate({expect, request, test}) {
        expect(this.topLine).toBeTruthy()

        await test.step('Проверка изображений баннера', async () => {
            expect(this.img).toBeInstanceOf(SettingsBannerImages)

            await this.img.validate({expect, request})
        })
    }
}

export class SettingsBannerImages extends BasicAPI {
    /**
     * @param img {Object} - Данные с апи запроса
     */
    constructor(img) {
        super();
        this.mobile = img.mobile
        this.desktop = img.desktop
    }

    /**
     * Валидация корректности полей
     * @param expect {Expect<{}>}
     * @param request {APIRequestContext}
     */
    async validate({expect, request}) {
        expect(this.desktop).toBeTruthy()
        expect(this.mobile).toBeTruthy()
        expect((await request.get(this.desktop)).ok()).toBeTruthy();
        expect((await request.get(this.mobile)).ok()).toBeTruthy();
    }
}

export class StylesSettings extends BasicAPI {
    /**
     * @param config {Object} - Данные с апи запроса
     */
    constructor (config) {
        super();
        this.footerData =  config.footerData ? new FooterData(config.footerData) : null
        this.customBackendAddress = config.customBackendAddress
        this.feedbackEmail = config.feedbackEmail
        this.logoImg = config.logoImg
        this.logoSmallImg = config.logoSmallImg
        this.banner =  config.banner ? new SettingsBanner(config.banner) : null
        this.sign =  config.sign ? new SettingsSign(config.sign) : null
    }

    /**
     * Валидация полей
     * @param expect {Expect<{}>}
     * @param request {APIRequestContext}
     * @param test {TestType<PlaywrightTestArgs & PlaywrightTestOptions, PlaywrightWorkerArgs & PlaywrightWorkerOptions>}
     */
    async validate({expect, request, test}) {
        await test.step('Проверка полей формы обратной связи', async () => {
            expect(this.feedbackEmail).toBeTruthy()

            if (this.customBackendAddress !== '') {
                expect((await request.get(`${this.customBackendAddress}/api/ping`)).ok()).toBeTruthy();
            }
        })

        await test.step('Проверка полей баннера', async () => {
            expect(this.banner).toBeInstanceOf(SettingsBanner)

            await this.banner.validate({expect, request, test})
        })

        await test.step('Проверка логотипов', async () => {
            expect(this.logoImg).toBeTruthy()
            expect(this.logoSmallImg).toBeTruthy()

            expect((await request.get(this.logoImg)).ok()).toBeTruthy();
            expect((await request.get(this.logoSmallImg)).ok()).toBeTruthy();
        })

        await test.step('Проверка полей для макета регистрации', async () => {
            expect(this.sign).toBeInstanceOf(SettingsSign)

            await this.sign.validate({expect, request})
        })

        await test.step('Проверка полей подвала', async () => {
            expect(this.footerData).toBeInstanceOf(FooterData)

            await this.footerData.validate({expect, request, test})
        })
    }
}